import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  styled
} from '@mui/material';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomDocViewEnterpriseSearch from '@components/docViewer/CustomDocViewEnterpriseSearch';
import { environment } from '@shared/config/environment';

const StyledTreeItem = styled(TreeItem)({
  '& .MuiTreeItem-iconContainer': {
    display: 'none',
  },
});

/** ---------- Incoming API Types ---------- */
export type ApiFile = {
  docId: string;
  fileName: string;
  blobUrl?: string;
  pdfBlobUrl?: string;
  docType?: string;
  pageCount?: number;
};

export type ApiSubDepartment = {
  subDepartmentId: string;
  subDepartment: string;
  files: ApiFile[];
};

export type ApiDepartment = {
  departmentId: string;
  department: string;
  subdepartments: ApiSubDepartment[];
};

export type ApiPayload = {
  departments: ApiDepartment[];
};

/** ---------- Explorer Node ---------- */
type Node = {
  id: string;
  name: string;
  isFile?: boolean;
  children?: Node[];
  meta?: ApiFile;
};

/** ---------- DocSource typing for the viewer ---------- */
type DocSource = {
  docId: string;
  name: string;
  pageImageUrls: string[];
  pdfUrl?: string;
  originalFileUrl?: string;
  pageCount?: number;
  docType?: string;
};

/** ---------- Helpers ---------- */
const stripExt = (s: string) => s.replace(/\.[^/.]+$/, '');

const buildPageUrls = (docId: string, pageCount?: number) =>
  !docId || !pageCount || pageCount < 1
    ? []
    : Array.from(
        { length: pageCount },
        (_, i) =>
          `${environment?.EnterpriceSearchServer ?? 'https://bytepad-enterprise-sear-api.azurewebsites.net'}/api/docs/${docId}/pages/${i + 1}`
      );

/** ---------- Tree + Map Builder ---------- */
const buildTreeAndMap = (data: ApiPayload) => {
  const map = new Map<string, Node>();

  const safeId = (prefix: string, ...parts: (string | number | undefined)[]) =>
    [
      prefix,
      ...parts
        .map((p) => (p == null ? '' : String(p)))
        .map((s) => s.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, ''))
        .filter(Boolean),
    ].join('_');

  const tree = (data.departments ?? []).map((d, di) => {
    const deptStable = d.departmentId || d.department || `idx-${di}`;
    const deptId = safeId('dept', deptStable);
    const deptNode: Node = {
      id: deptId,
      name: d.department,
      children: (d.subdepartments ?? []).map((s, si) => {
        const subStable = s.subDepartmentId || s.subDepartment || `${deptStable}-${si}`;
        const subId = safeId('sub', subStable);
        const subNode: Node = {
          id: subId,
          name: s.subDepartment,
          children: (s.files ?? []).map((f) => {
            const fileId = f.docId;
            const fileNode: Node = {
              id: fileId,
              name: f.fileName,
              isFile: true,
              meta: f,
            };
            map.set(fileId, fileNode);
            return fileNode;
          }),
        };
        map.set(subId, subNode);
        return subNode;
      }),
    };
    map.set(deptId, deptNode);
    return deptNode;
  });

  return { tree, map };
};

/** ---------- Component ---------- */
const FileExplorer: React.FC<{ data?: ApiPayload }> = ({ data: propData }) => {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [menu, setMenu] = useState<null | { mouseX: number; mouseY: number }>(null);
  
  // API state
  const [apiData, setApiData] = useState<ApiPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data from API
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const baseUrl = environment?.EnterpriceSearchServer ?? 'https://bytepad-enterprise-sear-api.azurewebsites.net/api';
        console.log("Fetching catalog from", baseUrl);
        const endpoint = `${baseUrl}/api/catalog/tree`;
        
        const response = await fetch(endpoint);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch catalog: ${response.statusText}`);
        }
        
        const data: ApiPayload = await response.json();
        setApiData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while fetching data');
        console.error('Error fetching catalog:', err);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if no prop data is provided
    if (!propData) {
      fetchCatalog();
    } else {
      setApiData(propData);
      setLoading(false);
    }
  }, [propData]);

  const payload = propData || apiData;
  const { tree, map } = useMemo(
    () => (payload ? buildTreeAndMap(payload) : { tree: [], map: new Map() }),
    [payload]
  );
  const selected = selectedId ? map.get(selectedId) : null;

  const [doc, setDoc] = useState<DocSource | null>(null);

  useEffect(() => {
    if (selectedId && !map.has(selectedId)) {
      setSelectedId(null);
      setDoc(null);
    }
  }, [map, selectedId]);

  useEffect(() => {
    if (!selected?.isFile || !selected.meta) {
      setDoc(null);
      return;
    }
    const m = selected.meta;

    setDoc({
      docId: m.docId,
      name: `${stripExt(m.fileName)} — ${m.docId.slice(0, 8)}`,
      pageImageUrls: buildPageUrls(m.docId, m.pageCount),
      pdfUrl: m.pdfBlobUrl,
      originalFileUrl: m.blobUrl,
      pageCount: m.pageCount,
      docType: m.docType,
    });
  }, [selected]);

  const handleToggle = (_: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  const handleSelect = (_: React.SyntheticEvent, nodeId: string) => {
    const n = map.get(nodeId);
    if (n?.isFile) setSelectedId(nodeId);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const render = (n: Node) => {
    const isExpanded = expanded.includes(n.id);
    const isSelected = selectedId === n.id;

    const onLabelClick = (e: React.MouseEvent) => {
      if (!n.isFile) {
        e.preventDefault();
        e.stopPropagation();
        toggleExpand(n.id);
      }
    };

    const onFolderIconClick = (e: React.MouseEvent) => {
      if (!n.isFile) {
        e.preventDefault();
        e.stopPropagation();
        toggleExpand(n.id);
      }
    };

    return (
      <StyledTreeItem
        key={n.id}
        nodeId={n.id}
        label={
          <Box
            onClick={onLabelClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              cursor: 'pointer',
              bgcolor: isSelected ? '#E3F2FD' : 'transparent',
              '&:hover': {
                bgcolor: isSelected ? '#BBDEFB' : theme.palette.action.hover,
              },
            }}
          >
            {n.isFile ? (
              <DescriptionIcon fontSize="small" />
            ) : isExpanded ? (
              <FolderOpenIcon
                fontSize="small"
                color="warning"
                onClick={onFolderIconClick}
              />
            ) : (
              <FolderIcon
                fontSize="small"
                color="warning"
                onClick={onFolderIconClick}
              />
            )}
            <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
              {n.name}
            </Typography>
          </Box>
        }
        onContextMenu={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setMenu(menu ? null : { mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
        }}
      >
        {n.children?.map(render)}
      </StyledTreeItem>
    );
  };

  return (
    <Grid container spacing={2}>
      {/* Left Sidebar */}
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            height: '80vh',
            p: 1,
            overflow: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.action.disabled,
              borderRadius: 8,
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : tree.length ? (
            <TreeView
              expanded={expanded}
              onNodeToggle={handleToggle}
              selected={selectedId ?? undefined}
              onNodeSelect={handleSelect}
              aria-label="document-explorer"
            >
              {tree.map(render)}
            </TreeView>
          ) : (
            <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
              No documents found
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Right Viewer */}
      <Grid item xs={12} md={9}>
        <Paper sx={{ height: '80vh', p: 0 }}>
          {doc ? (
            <CustomDocViewEnterpriseSearch source={doc} />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                {loading
                  ? 'Loading documents...'
                  : 'Please select a document from the left panel'}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Context Menu */}
      <Menu
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.mouseY, left: menu.mouseX } : undefined}
      >
        <MenuItem onClick={() => setMenu(null)}>Upload Document</MenuItem>
        <MenuItem onClick={() => setMenu(null)}>Delete Document</MenuItem>
        <MenuItem onClick={() => setMenu(null)}>Create Directory</MenuItem>
        <MenuItem onClick={() => setMenu(null)}>Delete Directory</MenuItem>
      </Menu>
    </Grid>
  );
};

export default FileExplorer;

